import maplibregl from 'maplibre-gl';
import { Threebox as ThreeboxCtor } from 'threebox-plugin';
import type { MapPlacedModel } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

const DEG2RAD = Math.PI / 180;
// Mapbox GL v1 default vertical field of view, in radians (~36.87°).
const MAPBOX_DEFAULT_FOV = 0.6435011087932844;

type BackendContext = {
  map: any;
  resolveContentUrl: (objUuid: string) => string | null;
};

/**
 * threebox-plugin was written for Mapbox GL JS. Its `CameraSync` reads a set of
 * transform internals (`_fov`, `_pitch`, `_maxPitch`, `scale`, `tileSize`,
 * `point`, `angle`, `centerOffset`, ...) that MapLibre GL 5.x keeps behind an
 * internal helper. We install best-effort getters for any that are missing so
 * the *legacy* (mapbox < 2.0) camera branch produces finite, correct matrices.
 *
 * Only missing keys are shimmed — existing MapLibre getters are never clobbered.
 */
function installLegacyTransformShim(map: any): void {
  const t = map?.transform;
  if (!t) return;

  const has = (key: string) => {
    try {
      return t[key] !== undefined && t[key] !== null;
    } catch {
      return false;
    }
  };

  const def = (key: string, getter: () => unknown) => {
    if (has(key)) return;
    try {
      Object.defineProperty(t, key, { get: getter, configurable: true });
    } catch {
      /* non-configurable — leave as-is */
    }
  };

  const zoom = () => (typeof map.getZoom === 'function' ? map.getZoom() : 0);
  const worldSize = () => {
    const tileSize = (t.tileSize as number) ?? 512;
    const scale = (t.scale as number) ?? Math.pow(2, zoom());
    return tileSize * scale;
  };

  def('_fov', () => MAPBOX_DEFAULT_FOV);
  def('_pitch', () => (map.getPitch?.() ?? 0) * DEG2RAD);
  def('_maxPitch', () =>
    typeof t.maxPitch === 'number' ? t.maxPitch : (map.getMaxPitch?.() ?? 60),
  );
  def('angle', () => -((map.getBearing?.() ?? 0) * DEG2RAD));
  def('tileSize', () => 512);
  def('scale', () => Math.pow(2, zoom()));
  def('centerOffset', () => ({ x: 0, y: 0 }));
  def('width', () => map.getCanvas?.()?.clientWidth ?? 0);
  def('height', () => map.getCanvas?.()?.clientHeight ?? 0);
  def('point', () => {
    const c = t.center ?? map.getCenter?.();
    if (!c) return { x: 0, y: 0 };
    const mc = maplibregl.MercatorCoordinate.fromLngLat({ lng: c.lng, lat: c.lat });
    const ws = worldSize();
    return { x: mc.x * ws, y: mc.y * ws };
  });
  // Guard for the `if (t.elevation) { ... t._camera.position[2] }` line when a
  // transform exposes a truthy elevation but no mapbox FreeCamera.
  def('_camera', () => ({ position: [0, 0, 0] }));
}

/**
 * Threebox-backed renderer for placed 3D models. Activates only when threebox
 * initializes healthily on the current MapLibre map; otherwise the caller keeps
 * using the native three.js renderer.
 */
export class ThreeboxBackend {
  private tb: any = null;
  private readonly map: any;
  private readonly ctx: BackendContext;
  private readonly objects = new Map<string, any>();
  private readonly loadingIds = new Set<string>();
  private lastSyncZoom = Number.NaN;
  active = false;

  constructor(ctx: BackendContext) {
    this.ctx = ctx;
    this.map = ctx.map;
  }

  /** Try to initialize threebox on the shared GL context. Returns true if healthy. */
  init(gl: WebGLRenderingContext): boolean {
    const canvas = this.map.getCanvas?.();
    // Preserve MapLibre's drawing-buffer sizing — threebox's WebGLRenderer
    // constructor calls setSize()/setPixelRatio() on the shared canvas.
    const prev = canvas
      ? { w: canvas.width, h: canvas.height, sw: canvas.style.width, sh: canvas.style.height }
      : null;

    try {
      installLegacyTransformShim(this.map);

      this.tb = new ThreeboxCtor(this.map, gl, {
        defaultLights: true,
        enableSelectingObjects: true,
        enableTooltips: false,
      });
      // Force the legacy (mapbox < 2.0) camera branch — MapLibre 5.x lacks the
      // v2 transform internals (_horizonShift, _camera.position, elevation.*).
      this.tb.mapboxVersion = 1.0;
      this.tb.cameraSync?.setupCamera?.();

      if (canvas && prev) {
        canvas.width = prev.w;
        canvas.height = prev.h;
        canvas.style.width = prev.sw;
        canvas.style.height = prev.sh;
      }

      if (!this.isHealthy()) {
        console.info(
          '[geobox-gl] Threebox produced non-finite camera matrices on this map; using native renderer.',
        );
        this.dispose();
        return false;
      }

      this.active = true;
      console.info('[geobox-gl] Threebox renderer active for 3D models.');
      return true;
    } catch (err) {
      console.warn('[geobox-gl] Threebox init failed; using native renderer.', err);
      if (canvas && prev) {
        try {
          canvas.width = prev.w;
          canvas.height = prev.h;
          canvas.style.width = prev.sw;
          canvas.style.height = prev.sh;
        } catch {
          /* ignore */
        }
      }
      this.dispose();
      return false;
    }
  }

  private isHealthy(): boolean {
    const el = this.tb?.camera?.projectionMatrix?.elements;
    if (!el || el.length < 16) return false;
    for (let i = 0; i < 16; i += 1) {
      if (!Number.isFinite(el[i])) return false;
    }
    const wm = this.tb?.world?.matrix?.elements;
    if (wm) {
      for (let i = 0; i < 16; i += 1) {
        if (!Number.isFinite(wm[i])) return false;
      }
    }
    return true;
  }

  render(): void {
    if (!this.active || !this.tb) return;
    try {
      this.tb.update();
    } catch (err) {
      console.warn('[geobox-gl] Threebox render failed; disabling threebox backend.', err);
      this.dispose();
    }
  }

  /** Reconcile threebox objects with the current placed-model list. */
  sync(models: MapPlacedModel[], selectedId: string | null): void {
    if (!this.active || !this.tb) return;
    const zoom = this.map.getZoom?.() ?? 0;
    this.lastSyncZoom = zoom;

    const wanted = new Set<string>();
    for (const model of models) {
      if (model.visible === false) continue;
      if (model.layer_bound || model.layer_name) continue;
      if (zoom < model.min_zoom || zoom > model.max_zoom) continue;
      wanted.add(model.id);
      this.ensureObject(model);
    }

    for (const [id, obj] of [...this.objects]) {
      if (!wanted.has(id)) {
        try {
          this.tb.remove(obj);
        } catch {
          /* ignore */
        }
        this.objects.delete(id);
      }
    }

    for (const model of models) {
      const obj = this.objects.get(model.id);
      if (!obj) continue;
      this.applyTransform(obj, model, selectedId);
    }
  }

  /** True when a re-sync is needed because the zoom crossed a visibility edge. */
  needsResync(): boolean {
    if (!this.active) return false;
    const zoom = this.map.getZoom?.() ?? 0;
    return zoom !== this.lastSyncZoom;
  }

  private applyTransform(obj: any, model: MapPlacedModel, selectedId: string | null): void {
    try {
      obj.setCoords([model.loc[0], model.loc[1], model.loc[2] ?? 0]);
      obj.rotation.set(model.rotation[0] ?? 0, model.rotation[1] ?? 0, model.rotation[2] ?? 0);
      const s = Math.max(model.scale ?? 1, 0.01);
      obj.scale.set(s, s, s);
      // Avoid re-assigning selected=false (clears hover wireframe). Also avoid
      // Threebox's remove(boxGroup) disposing shared over/selected materials.
      if (!obj.__geoboxWireframeRemovePatched && typeof obj.remove === 'function') {
        const original = obj.remove.bind(obj);
        obj.remove = (child: any) => {
          if (child?.name === 'boxGroup') {
            try {
              obj.scaleGroup?.remove?.(child);
            } catch {
              /* ignore */
            }
            return obj;
          }
          return original(child);
        };
        obj.__geoboxWireframeRemovePatched = true;
      }
      const nextSelected = model.id === selectedId;
      if (obj.selected !== nextSelected) {
        obj.selected = nextSelected;
        if (!nextSelected && obj.over) {
          obj.over = false;
          obj.over = true;
        }
      }
    } catch {
      /* ignore transient transform errors */
    }
  }

  private ensureObject(model: MapPlacedModel): void {
    if (this.objects.has(model.id) || this.loadingIds.has(model.id)) return;
    const url = this.ctx.resolveContentUrl(model.obj);
    if (!url) return;

    this.loadingIds.add(model.id);
    const options = {
      type: 'gltf',
      obj: url,
      units: 'meters',
      anchor: 'center',
      rotation: { x: 90, y: 180, z: 0 },
      scale: 1,
    };

    try {
      this.tb.loadObj(options, (loaded: any) => {
        try {
          if (loaded?.userData) loaded.userData.mapModelId = model.id;
          loaded.name = model.id;
          this.applyTransform(loaded, model, null);
          this.tb.add(loaded);
          this.objects.set(model.id, loaded);
        } finally {
          this.loadingIds.delete(model.id);
          this.map.triggerRepaint?.();
        }
      });
    } catch (err) {
      this.loadingIds.delete(model.id);
      console.warn('[geobox-gl] Threebox loadObj failed', model.obj, err);
    }
  }

  clear(): void {
    if (!this.tb) return;
    for (const obj of this.objects.values()) {
      try {
        this.tb.remove(obj);
      } catch {
        /* ignore */
      }
    }
    this.objects.clear();
    this.loadingIds.clear();
  }

  dispose(): void {
    try {
      this.clear();
      this.tb?.dispose?.();
    } catch {
      /* ignore */
    }
    this.tb = null;
    this.active = false;
  }
}
